import React from 'react';
import type { CollateralData } from '../../api/client';

interface Props {
  coll: CollateralData;
  selected: boolean;
  onToggle: (id: number | string) => void;
  onPreview: () => void;
  onAnalytics: () => void;
}

const TYPE_ICONS: Record<string, string> = {
  pdf: 'ti-file-type-pdf', ppt: 'ti-presentation', video: 'ti-player-play',
  image: 'ti-photo', url: 'ti-world-www', xls: 'ti-table', doc: 'ti-file-text',
};

const TYPE_THUMB_STYLE: Record<string, React.CSSProperties> = {
  pdf:   { background: 'linear-gradient(135deg,#FFF5F5,#FED7D7)', color: '#C53030' },
  ppt:   { background: 'linear-gradient(135deg,#FFF3E0,#FFD49A)', color: '#C05621' },
  video: { background: 'linear-gradient(135deg,#1A202C,#2D3748)',  color: 'white'   },
  image: { background: 'linear-gradient(135deg,#FAF5FF,#E9D8FD)', color: '#6B46C1' },
  url:   { background: 'linear-gradient(135deg,#F0FFF4,#C6F6D5)', color: '#276749' },
  xls:   { background: 'linear-gradient(135deg,#F0FFF4,#C6F6D5)', color: '#276749' },
};

export function getAvgReadPct(coll: CollateralData): number {
  if (!coll.recipients?.length) return 0;
  return Math.round(coll.recipients.reduce((a, r) => a + (r.readPct || 0), 0) / coll.recipients.length);
}

export default function CollateralCard({ coll, selected, onToggle, onPreview, onAnalytics }: Props) {
  const collId = coll._id || String(coll.id);
  const avgPct = getAvgReadPct(coll);
  const ringColor = avgPct === 100 ? '#22C55E' : avgPct > 0 ? '#C8973A' : '#CBD5E0';
  const circumference = 2 * Math.PI * 14;
  const offset = circumference - (avgPct / 100) * circumference;
  const totalRecips = coll.recipients?.length || 0;
  const readCount = coll.recipients?.filter(r => r.readPct === 100).length || 0;
  const thumbStyle = TYPE_THUMB_STYLE[coll.type] || { background: '#F1F4F8', color: '#718096' };
  const recipColors = ['#0F1B2D', '#3B82F6', '#7C3AED', '#22C55E', '#EF4444'];

  return (
    <div onClick={() => onToggle(collId)}
      style={{ background: 'white', border: `${selected ? 2 : 1.5}px solid ${selected ? '#C8973A' : '#E2E8F0'}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'all .18s', position: 'relative' }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = '#C8973A'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.09)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
      {selected && <div style={{ position: 'absolute', top: '8px', left: '8px', width: '22px', height: '22px', background: '#C8973A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}><i className="ti ti-check" style={{ fontSize: '13px', color: 'white' }} /></div>}
      <div style={{ height: '108px', display: 'flex', alignItems: 'center', justifyContent: 'center', ...thumbStyle }}>
        <i className={`ti ${TYPE_ICONS[coll.type] || 'ti-file'}`} style={{ fontSize: '34px', opacity: .65 }} />
      </div>
      {totalRecips > 0 ? (
        <div style={{ position: 'absolute', top: '8px', right: '8px', width: '34px', height: '34px' }}>
          <svg width="34" height="34" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="18" cy="18" r="14" fill="none" stroke="#E2E8F0" strokeWidth="3" />
            <circle cx="18" cy="18" r="14" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: avgPct === 100 ? '#22C55E' : avgPct > 0 ? '#C8973A' : '#718096' }}>
            {avgPct > 0 ? `${avgPct}%` : <i className="ti ti-eye-off" style={{ fontSize: '12px' }} />}
          </div>
        </div>
      ) : (
        <div style={{ position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '50%', background: '#F1F4F8', border: '1.5px solid #CBD5E0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ti ti-share" style={{ fontSize: '12px', color: '#718096' }} />
        </div>
      )}
      <div style={{ padding: '10px 11px 9px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#0F1B2D', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={coll.name}>{coll.name}</div>
        <div style={{ fontSize: '10px', color: '#718096', display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
          {coll.size}{coll.pages > 1 ? ` · ${coll.pages}p` : ''}
          {coll.tags.map(t => <span key={t} style={{ background: '#EDF2F7', color: '#4A5568', fontSize: '9px', fontWeight: '500', padding: '2px 6px', borderRadius: '4px' }}>{t}</span>)}
        </div>
        {totalRecips > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px' }}>
            <div style={{ display: 'flex' }}>
              {coll.recipients.slice(0, 3).map((r, i) => (
                <div key={i} style={{ width: '18px', height: '18px', borderRadius: '50%', fontSize: '7px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white', flexShrink: 0, marginLeft: i > 0 ? '-5px' : 0, background: recipColors[i % recipColors.length], color: 'white' }}>{r.ini}</div>
              ))}
            </div>
            <span style={{ fontSize: '10px', color: '#718096' }}>{readCount}/{totalRecips} read</span>
          </div>
        )}
        <div style={{ fontSize: '10px', color: '#718096', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #E2E8F0' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#0F1B2D', color: '#E8B55A', fontSize: '7px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{coll.sharedByIni}</div>
          <span>{coll.sharedBy}</span><span style={{ opacity: .5 }}>·</span><span>{coll.sharedDate}</span>
        </div>
      </div>
      <div style={{ display: 'flex', borderTop: '1px solid #E2E8F0' }} onClick={e => e.stopPropagation()}>
        <button onClick={onPreview} style={{ flex: 1, padding: '7px 4px', background: 'none', border: 'none', fontSize: '11px', color: '#4A5568', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRight: '1px solid #E2E8F0' }}>
          <i className="ti ti-eye" /> Preview
        </button>
        <button onClick={onAnalytics} style={{ flex: 1, padding: '7px 4px', background: 'none', border: 'none', fontSize: '11px', color: '#4A5568', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          <i className="ti ti-chart-bar" /> Analytics
        </button>
      </div>
    </div>
  );
}
